import { LeadCreateRequest, LeadGroupLeadsMarkersResponse, LeadMarkerListResponse, LeadMarkerResponse, LeadPatchRequest, LeadResponse, LeadStatusChangeResponse, RepLastLeadPositionRM, RogersLeadsConversionDetailReport, RogersLeadsConversionReport, RogersLeadsRepStatisticsRM } from '@herbjoy/apex-api-models';
import { IHttpService } from 'angular';
import { DivisionEnum } from './division.service';

/**
 * An enumeration of types that can describe a lead status. Statuses are flags,
 * and can be combined in any way.
 */
export enum StatusTypeEnum {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Interaction = 1,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Observation = 2,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Declaration = 4
}

export type LeadMarker = LeadMarkerResponse & { Color: string };

export class LeadsService {
    statusTypeEnum: typeof StatusTypeEnum;
    constructor(private $http: IHttpService, private apexApiUrl: string) {
        'ngInject';
        this.statusTypeEnum = StatusTypeEnum;
    }

    /**
     * Retrieve leads that match the filters provided in the given query object.
     * @param {any} query The filter parameters for the query
     * @returns {Promise<any>} The list of leads.
     */
    retrieveLeads(query) {
        return this.$http<LeadResponse>({
            method: "GET",
            url: this.apexApiUrl + "leads",
            params: query,
            headers: { Accept: "application/json" }
        })
            .then(response => {
                return response.data;
            })
    }

    retrieveLeadMarkers(query) {
        return this.$http<LeadMarkerListResponse>({
            method: "GET",
            url: this.apexApiUrl + "lead-markers",
            params: query,
            headers: { Accept: "application/json" }
        })
            .then(response => {
                const leadMarkerList = response.data;
                return hydrateLeadMarkerList(leadMarkerList)
            })
            .then(leads => {
                // Map the response data into VM format.
                return leads.map(lead => {
                    const marker = mapLeadSnapshotVM(lead);
                    return marker;
                });
            });
    }

    /**
     * Retrieve the lead with the given ID.
     * @param leadID The primary key for the lead.
     * @returns The promise resolving to the lead.
     */
    retrieveLead(leadID: number) {
        return this.$http<LeadResponse>({
            method: "GET",
            url: this.apexApiUrl + "leads/" + leadID,
            headers: { Accept: "application/json" }
        })
            .then(function (response) {
                return response.data;
            })
    }

    /**
     * Create a new lead from the given request.
     * @param leadCreateRequest The request body
     * @returns The result of the create request.
     */
    createLead(leadCreateRequest: LeadCreateRequest) {
        return this.$http<LeadResponse>({
            method: "POST",
            url: this.apexApiUrl + "leads",
            data: leadCreateRequest,
            headers: { "Content-Type": "application/json", Accept: "application/json" }
        });
    }

    /**
     * Bulk create or update all leads for each request in the list.
     * @param {any} leadCreateRequests The request body
     * @returns {Promise} The result of the create request
     */
    bulkCreateLeads(leadCreateRequests: LeadCreateRequest) {
        return this.$http({
            method: "POST",
            url: this.apexApiUrl + "leads/bulk",
            data: leadCreateRequests,
            headers: { "Content-Type": "application/json" }
        });
    }

    /**
     * Patch the lead with the given ID with the provided patch data.
     * @param leadID The lead being updated.
     * @param leadPatchRequest The request body
     * @returns The result of the patch request.
     */
    patchLead(leadID, leadPatchRequest: LeadPatchRequest) {
        return this.$http<void>({
            method: "PATCH",
            url: this.apexApiUrl + "leads/" + leadID,
            data: leadPatchRequest,
            headers: { "Content-Type": "application/json" }
        });
    }

    /**
    * Patch the given leads with the provided patch data.
    * @param {any} leadPatchRequest The request body
    * @returns {Promise} The result of the patch.
    */
    bulkPatchLead(leadPatchRequest: LeadPatchRequest) {
        return this.$http<void>({
            method: "PATCH",
            url: this.apexApiUrl + "leads/bulk",
            data: leadPatchRequest,
            headers: { "Content-Type": "application/json" }
        });
    }

    // return {
    //     retrieveRepLastLeadPositionsReport: retrieveRepLastLeadPositionsReport,
    //     retrieveRepLeadsActivity: retrieveRepLeadsActivity,
    // };
    retrieveLeadsConversionReport(query) {
        return this.$http<RogersLeadsConversionReport>({
            method: "GET",
            url: this.apexApiUrl + "reports/leads-conversion",
            params: query,
            headers: { Accept: "application/json" }
        })
            .then(function (response) {
                return response.data;
            })
    }

    retrieveLeadsConversionDetailReport(query) {
        return this.$http<RogersLeadsConversionDetailReport>({
            method: "GET",
            url: this.apexApiUrl + "reports/leads-conversion-detail",
            params: query,
            headers: { Accept: "application/json" }
        })
            .then(function (response) {
                return response.data;
            });
    }

    retrieveLeadsActivityReport(query) {
        return this.$http<RogersLeadsRepStatisticsRM>({
            method: "GET",
            url: this.apexApiUrl + "reports/rogers/lead-group-rep-statistics",
            params: query,
            headers: { Accept: "application/json" }
        })
            .then(function (response) {
                return response.data;
            })
    }

    retrieveLeadGroupLeads(query) {
        return this.$http<LeadGroupLeadsMarkersResponse>({
            method: "GET",
            url: this.apexApiUrl + "lead-territories/lead-markers",
            params: query,
            headers: { Accept: "application/json" }
        })
            .then(function (response) {
                const leadMarkerList = response.data;
                const leads = hydrateLeadMarkerList(leadMarkerList);
                const markers = leads.map(function (lead) {
                    const marker = mapLeadSnapshotVM(lead);
                    return marker;
                });
                return {
                    Leads: markers,
                    TerritoriesLeadsCount: response.data.TerritoriesLeadsCounts
                };
            });
    }

    /**
     * Get the list of special data keys to be displayed prominently for leads
     * under the given division.
     * @param {number} divisionID The division for which the list of data keys will be loaded.
     */
    getLeadDataKeysByDivision(divisionID: number) {
        let keys: { field: string, label: string }[];
        switch (divisionID) {
            case DivisionEnum.Rogers: {
                const promoCodeFields = ["New Offer", "New"]
                keys = promoCodeFields.map(field => ({ field, label: "Promo Code" }))
                break;
            }
            default:
                keys = [];
        }
        return keys;
    }

    retrieveRepLastLeadPositionsReport(params) {
        return this.$http<RepLastLeadPositionRM>({
            method: "GET",
            url: `${this.apexApiUrl}/reports/rep-last-lead-positions`,
            params
        }).then(response => response.data)
    }

    retrieveRepLeadsActivity(employeeID, params) {
        return this.$http<LeadStatusChangeResponse[]>({
            method: "GET",
            url: `${this.apexApiUrl}/employees/${employeeID}/leads-activity`,
            params: params
        }).then(response => response.data)
    }
}

/**
 * Hydrate the list of leads within the normalized lead marker list response.
 * @param {any} leadMarkerList A lead marker list response.
 */
export function hydrateLeadMarkerList(leadMarkerList: LeadMarkerListResponse) {
    for (const lead of leadMarkerList.Leads) {
        const currentStatus = leadMarkerList.LeadStatuses.find(p => p.LeadStatusID === lead.LeadStatusID)
        const declaredStatus = leadMarkerList.LeadStatuses.find(p => p.LeadStatusID === lead.DeclaredStatusID)
        if (currentStatus) {
            Object.assign(lead, {
                LeadStatusID: lead.LeadStatusID,
                LeadStatusName: currentStatus.Name,
                Color: currentStatus.Color,
                Hue: currentStatus.Hue
            })
        }
        if (declaredStatus) {
            Object.assign(lead, {
                DeclarationStatusID: declaredStatus.LeadStatusID,
                DeclarationStatusName: declaredStatus.Name,
                DeclarationStatusColor: declaredStatus.Color,
                DeclarationStatusHue: declaredStatus.Hue
            })
        }
    }
    return leadMarkerList.Leads
}

/**
 * Map the given lead snapshot response data into a lead snapshot VM.
 * @param {any} lead A lead snapshot response from the lead group leads API
 * @returns {any} The lead snapshot mapped into view model format.
 */
export function mapLeadSnapshotVM(lead) {
    const vm = {
        LeadID: lead.LeadID,
        AssignedOwners: [],
        AssignedReps: [],
        LeadActivity: lead.LeadActivityHistory,
        LeadGroupID: lead.LeadGroupID,
        CurrentStatus: lead.LeadStatusID ? {
            LeadStatusID: lead.LeadStatusID,
            Name: lead.LeadStatusName,
            Color: lead.Color
        } : null,
        DeclarationStatus: lead.DeclarationStatusID ? {
            LeadStatusID: lead.DeclarationStatusID,
            Name: lead.DeclarationStatusName,
            Color: lead.DeclarationStatusColor
        } : null,
        Latitude: lead.Latitude,
        Longitude: lead.Longitude,
        Color: lead.CurrentStatus ? lead.CurrentStatus.Color : null
    }

    return vm
}
